import type { CreateCustomerInput } from '@clockwise/clients/customers';
import { ApiError } from '@clockwise/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { CustomFieldsEditor } from '@/components/custom/custom-fields-editor';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { customersApi } from '@/lib/api';
import { useUnitsWhere } from '@/lib/business-units';
import { isForbidden } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import { useObjectCustomFields } from '@/lib/use-object-custom-fields';
import {
  blankCustomerValues,
  blankToNull,
  blankToUndef,
  CustomerFormFields,
  CustomerFormSchema,
  type CustomerFormValues,
  classificationPayload,
  customerFormToPatch,
  customerToFormValues,
  isCustomerFormField,
  normalizeAddress,
  parentCustomerStandIn,
} from './customer-form-fields';

// Create AND edit, in one sheet — the two used to fork (create here, edit on
// a full-page route), which read as two different products for one action.
// Amending a PAST VERSION is still its own full-page route
// (`/customers/$id/edit?version=`): it's an admin-tier, permission-gated
// rewrite of history with its own explanatory banner, not a quick edit, and
// stays a deliberate destination rather than something you can fall into via
// a slide-over.
interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Omit to create a new customer; pass an id to edit that one in place. */
  customerId?: string;
  /** Creating: the Business Unit picked to start with (the list's filter). */
  defaultUnitId?: string;
}

export function CustomerFormSheet({ open, onOpenChange, customerId, defaultUnitId }: Props) {
  const queryClient = useQueryClient();
  // Creating: among the units where the caller's role may add customers
  // (what the server allows); it starts at the list's filter when that is one
  // of them, else the only one. Editing: the customer's own, shown read-only.
  const addUnits = useUnitsWhere('customer.edit');
  const showsUnits = addUnits.length > 1;
  const startUnit =
    defaultUnitId && addUnits.some((u) => u.id === defaultUnitId)
      ? defaultUnitId
      : addUnits.length === 1
        ? addUnits[0]?.id
        : undefined;

  // Gated on `open` too: this sheet is mounted once at the page and reused
  // for every row, so fetching only while it's actually visible keeps a
  // click on row 2 from paying for whatever row 1's query left behind.
  const detail = useQuery({
    queryKey: queryKeys.customers.detail(customerId ?? ''),
    queryFn: () => customersApi.get(customerId as string),
    enabled: customerId != null && open,
  });
  const customer = customerId != null ? (detail.data?.data ?? null) : null;

  const form = useForm<CustomerFormValues>({
    resolver: zodResolver(CustomerFormSchema),
    defaultValues: blankCustomerValues,
  });

  const cf = useObjectCustomFields('customer', customer?.customFields);

  // Blank on every open when creating; seeded from the fetched record once it
  // resolves when editing. Keyed off `open` (not just the id) so reopening
  // the sheet on the SAME row re-seeds from what's now on the server instead
  // of showing whatever was left in the form from the last visit.
  useEffect(() => {
    if (!open) return;
    if (customerId == null) {
      form.reset({ ...blankCustomerValues, businessUnitId: startUnit ?? '' });
      return;
    }
    if (customer) form.reset(customerToFormValues(customer));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, customerId, customer?.id, customer?.updatedAt]);

  const mutation = useMutation({
    mutationFn: (values: CustomerFormValues): Promise<{ data: unknown }> => {
      if (customerId != null) {
        return customersApi.patch(customerId, customerFormToPatch(values, cf.values));
      }
      const payload: CreateCustomerInput = {
        name: values.name,
        legalName: blankToUndef(values.legalName),
        qbId: values.qbId,
        phone: blankToUndef(values.phone),
        billingContactId: blankToUndef(values.billingContactId),
        creditTerm: blankToUndef(values.creditTerm),
        startDate: blankToUndef(values.startDate),
        departmentId: blankToUndef(values.departmentId),
        defaultInvoiceType: blankToUndef(values.defaultInvoiceType),
        corporateCalendar: blankToUndef(values.corporateCalendar),
        taxSpoc: blankToUndef(values.taxSpoc),
        invoiceNotes: blankToUndef(values.invoiceNotes),
        ...classificationPayload(values),
        // Create has no "leave unchanged" state, so blankToUndef would do here
        // too — blankToNull is used for symmetry with the edit path, where the
        // difference is load-bearing.
        parentCustomerId: blankToNull(values.parentCustomerId),
        isInternal: values.isInternal ?? false,
        address: normalizeAddress(values.address),
        billingAddress: normalizeAddress(values.billingAddress),
        businessUnitId: blankToUndef(values.businessUnitId),
      };
      return customersApi.create(payload);
    },
    onSuccess: () => {
      toast.success(customerId != null ? 'Customer updated' : 'Customer created');
      queryClient.invalidateQueries({ queryKey: queryKeys.customers.all });
      if (customerId != null) {
        queryClient.invalidateQueries({ queryKey: queryKeys.customers.detail(customerId) });
        queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      }
      onOpenChange(false);
    },
    onError: (err) => {
      const hadCfErrors = customerId != null && cf.handleApiError(err);
      if (err instanceof ApiError && err.fields?.length) {
        for (const fe of err.fields) {
          if (fe.path.startsWith('customFields.')) continue;
          if (isCustomerFormField(fe.path)) {
            form.setError(fe.path as keyof CustomerFormValues, { message: fe.message });
          }
        }
        toast.error('Please fix the highlighted fields');
        return;
      }
      if (hadCfErrors) {
        toast.error('Please fix the highlighted fields');
        return;
      }
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast.error(msg);
    },
  });

  const onSubmit = form.handleSubmit((v) => {
    cf.reset();
    // Creating, with several units: the customer names its own; the server
    // would refuse to guess.
    if (customerId == null && showsUnits && !v.businessUnitId) {
      form.setError('businessUnitId', { message: 'Choose a Business Unit.' });
      return;
    }
    mutation.mutate(v);
  });

  const { requestOpenChange, discardDialog } = useUnsavedChangesGuard({
    open,
    isDirty: form.formState.isDirty,
    isSubmitting: mutation.isPending,
    onOpenChange,
  });

  // Presence-only permission check gates the trigger (list row / detail
  // page), not scope — a caller on My team (`my_team`) still sees
  // the pencil on a customer outside it, and the server 403s the load. Same
  // gap the old edit route had; surfaced here rather than silently blanking
  // the form.
  const forbidden = customerId != null && detail.isError && isForbidden(detail.error);
  const loadFailed = customerId != null && detail.isError && !forbidden;
  const loading = customerId != null && !customer && !detail.isError;

  return (
    <>
      <Sheet open={open} onOpenChange={requestOpenChange}>
        <SheetContent className="flex flex-col gap-0 sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>
              {customerId != null ? `Edit ${customer?.name ?? 'customer'}` : 'Add customer'}
            </SheetTitle>
            <SheetDescription>
              {customerId != null
                ? 'Update this customer account.'
                : 'Create a new customer account.'}
            </SheetDescription>
          </SheetHeader>

          {forbidden ? (
            <div className="flex-1 px-4 py-2">
              <p className="text-sm text-destructive">
                You don't have permission to edit this customer.
              </p>
            </div>
          ) : loadFailed ? (
            <div className="flex-1 px-4 py-2">
              <p className="text-sm text-destructive">
                {detail.error instanceof ApiError
                  ? detail.error.message
                  : 'Failed to load customer.'}
              </p>
            </div>
          ) : loading ? (
            <div className="flex-1 space-y-3 overflow-y-auto px-4 py-2">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-24 w-full" />
            </div>
          ) : (
            <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
              <div className="flex-1 space-y-6 overflow-y-auto px-4">
                <CustomerFormFields
                  form={form}
                  parentCustomer={customerId != null ? parentCustomerStandIn(customer) : undefined}
                  unitPickable={customerId == null}
                />
                {customerId != null ? (
                  <CustomFieldsEditor {...cf.EditorProps} disabled={mutation.isPending} />
                ) : null}
              </div>

              <SheetFooter className="flex-row justify-end border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => requestOpenChange(false)}
                  disabled={mutation.isPending}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={mutation.isPending}>
                  {mutation.isPending ? 'Saving…' : customerId != null ? 'Save' : 'Create customer'}
                </Button>
              </SheetFooter>
            </form>
          )}
        </SheetContent>
      </Sheet>
      {discardDialog}
    </>
  );
}
